import type { Bounds, LinearConfig } from 'wucdbm-exponential-slider'
import { computed, type Ref, toValue, type WritableComputedRef } from 'vue'
import { isNumber } from 'remeda'
import type { RefOrGetter } from '@/helpers'
import { useStepModel } from 'wucdbm-vue-exponential-slider'
import type {
    InputRangeModel,
    InputRangeTwoKnobbedValueFormatter,
} from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'

export type ExponentialSlider = {
    model: Ref<InputRangeModel>
    formatter: InputRangeTwoKnobbedValueFormatter
}

const modelBeautifier = (model: number) => {
    return Math.round(model)
}

export function useExponentialSlider(
    model: Ref<Bounds>,
    steps: RefOrGetter<number>,
    bounds: RefOrGetter<Bounds>,
    linearConfig: RefOrGetter<LinearConfig>,
    formatter: InputRangeTwoKnobbedValueFormatter | undefined,
    fixModelOnInit: boolean
): ExponentialSlider {
    const {
        model: stepModel,
        stepToModel,
        modelToStep,
    } = useStepModel(model, steps, bounds, linearConfig, {
        fixModelOnInit: fixModelOnInit,
        watchBounds: false,
        modelBeautifier,
    })

    const sliderModel = useSliderModelForSteps(stepModel, steps)

    const wrappedFormatter: InputRangeTwoKnobbedValueFormatter = (
        step,
        index
    ): string => {
        const modelValue = index === 1 ? model.value.max : model.value.min

        const modelStep = modelToStep(modelValue)

        const value =
            modelStep === step ? modelValue : modelBeautifier(stepToModel(step))

        if (formatter) {
            return `${formatter(value, index)}`
        }

        return String(value)
    }

    return {
        model: sliderModel,
        formatter: wrappedFormatter,
    }
}

export function useSliderModelForSteps(
    stepModel: Ref<Bounds>,
    steps: RefOrGetter<number>
): WritableComputedRef<InputRangeModel> {
    return useSliderModel(stepModel, () => {
        return {
            min: 0,
            max: toValue(steps),
        }
    })
}

export function useSliderModel(
    model: Ref<Bounds>,
    bounds: RefOrGetter<Bounds>
): WritableComputedRef<InputRangeModel> {
    return computed({
        get(): InputRangeModel {
            return [model.value.min, model.value.max]
        },
        set(v: InputRangeModel) {
            const min = v[0]
            const max = v[1]
            const boundsValue = toValue(bounds)

            model.value = {
                min: isNumber(min) ? min : boundsValue.min,
                max: isNumber(max) ? max : boundsValue.max,
            }
        },
    })
}
